import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { MagnifyingGlassIcon, EyeIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { Layout } from '@components';
import { getOrders, downloadOrderPdf } from '@services/admin.service';
import { Order, OrderItem, OrderItemConfiguration, OrderProductType } from '@utilities/types';
import { getConfigurationSections, getLegacyOrderItem, getMeasurementSections, PresentableOrderItem } from '@utilities/orderPresentation';

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
    confirmed: { label: 'Confirmée', className: 'badge-success' },
    pending: { label: 'En attente', className: 'badge-warning' },
    pending_payment: { label: 'En attente paiement', className: 'badge-warning' },
    cancelled: { label: 'Annulée', className: 'badge-error' },
};

const PAYMENT_STATUS_LABELS: Record<string, { label: string; className: string }> = {
    paid: { label: 'Payée', className: 'badge-success' },
    pending: { label: 'En attente', className: 'badge-warning' },
    deferred: { label: 'Différé', className: 'badge-info' },
    failed: { label: 'Échec', className: 'badge-error' },
    cancelled: { label: 'Annulé', className: 'badge-ghost' },
};

const SUIT_PIECE_LABELS: Record<string, string> = { '2_pieces': '2 pièces', '3_pieces': '3 pièces' };

const PRODUCT_LABELS: Record<OrderProductType, string> = {
    suit: 'Costume',
    shirt: 'Chemise',
    vest: 'Gilet',
};

const isMultiProductOrder = (order: Order): boolean =>
    order.schemaVersion === 2 || (Array.isArray(order.items) && order.items.length > 0);

const getConfigString = (configuration: OrderItemConfiguration, keys: string[]): string | null => {
    for (const key of keys) {
        const value = configuration[key];
        if (typeof value === 'string' && value.trim()) return value;
    }
    return null;
};

const getOrderTypeLabel = (order: Order): string => {
    if (!isMultiProductOrder(order)) return order.suitPiece ? (SUIT_PIECE_LABELS[order.suitPiece] || order.suitPiece) : 'Costume';
    const counts = (order.items || []).reduce<Record<OrderProductType, number>>(
        (acc, item) => ({ ...acc, [item.productType]: acc[item.productType] + (item.quantity || 1) }),
        { suit: 0, shirt: 0, vest: 0 },
    );
    return (Object.keys(counts) as OrderProductType[])
        .filter((type) => counts[type] > 0)
        .map((type) => `${counts[type]} ${PRODUCT_LABELS[type].toLowerCase()}${counts[type] > 1 ? 's' : ''}`)
        .join(' · ') || 'Panier vide';
};

const getOrderFabricLabel = (order: Order): string => {
    if (!isMultiProductOrder(order)) return order.tissuId || '—';
    const references = (order.items || [])
        .map((item) => getConfigString(item.configuration, [
            'tissuId', 'selectedTissuId', 'fabricId', 'shirtColor', 'selectedChemiseColor',
            'vestFabric', 'selectedVestFabric',
        ]))
        .filter((value): value is string => !!value);
    return Array.from(new Set(references)).join(', ') || '—';
};

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export const Orders: FC = () => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Pagination & search
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Modal
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const [downloadingPdfId, setDownloadingPdfId] = useState<string | null>(null);

    const handleDownloadPdf = useCallback(async (orderId: string) => {
        try {
            setDownloadingPdfId(orderId);
            const blob = await downloadOrderPdf(orderId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `commande-${orderId}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();
        } catch {
            setError('Erreur lors du téléchargement du PDF');
        } finally {
            setDownloadingPdfId(null);
        }
    }, []);

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getOrders();
            setOrders(res.data);
        } catch (err: any) {
            setError(err.message || 'Erreur lors du chargement des commandes');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    // Filtered orders
    const filtered = useMemo(() => {
        if (!search.trim()) return orders;
        const q = search.toLowerCase();
        return orders.filter((o) => {
            const clientName = o.client ? `${o.client.firstName} ${o.client.lastName}`.toLowerCase() : '';
            const clientEmail = o.client?.email?.toLowerCase() || '';
            const memberName = o.member ? `${o.member.firstName} ${o.member.lastName}`.toLowerCase() : '';
            const memberEmail = o.member?.email?.toLowerCase() || o.memberEmail?.toLowerCase() || '';
            const date = new Date(o.createdAt).toLocaleDateString('fr-FR');
            const status = STATUS_LABELS[o.status]?.label.toLowerCase() || o.status;
            const productSummary = getOrderTypeLabel(o).toLowerCase();
            const fabricSummary = getOrderFabricLabel(o).toLowerCase();
            const itemConfiguration = o.items ? JSON.stringify(o.items).toLowerCase() : '';
            return clientName.includes(q) || clientEmail.includes(q) || memberName.includes(q)
                || memberEmail.includes(q) || date.includes(q) || status.includes(q)
                || productSummary.includes(q) || fabricSummary.includes(q) || itemConfiguration.includes(q);
        });
    }, [orders, search]);

    // Reset page on search/rowsPerPage change
    useEffect(() => { setPage(1); }, [search, rowsPerPage]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
    const paginated = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <span className="loading loading-spinner loading-lg"></span>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold">Commandes</h1>
                    <span className="text-sm text-base-content/50">{filtered.length} commande{filtered.length !== 1 ? 's' : ''}</span>
                </div>

                {error && (
                    <div className="alert alert-error mb-4">
                        <span>{error}</span>
                    </div>
                )}

                {/* Barre de recherche + lignes par page */}
                <div className="flex items-center justify-between gap-4 mb-4">
                    <div className="relative flex-1 max-w-sm">
                        <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
                        <input
                            type="text"
                            placeholder="Rechercher..."
                            className="input input-bordered input-sm w-full pl-9"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-base-content/50">Lignes :</span>
                        <select
                            className="select select-bordered select-sm"
                            value={rowsPerPage}
                            onChange={(e) => setRowsPerPage(Number(e.target.value))}
                        >
                            {ROWS_PER_PAGE_OPTIONS.map((n) => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="overflow-x-auto bg-white rounded-lg border border-base-300">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Membre (Boutique)</th>
                                <th>Client</th>
                                <th>Type</th>
                                <th>Tissu</th>
                                <th>Statut</th>
                                <th>Paiement</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginated.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="text-center text-base-content/50 py-8">
                                        Aucune commande trouvée
                                    </td>
                                </tr>
                            ) : (
                                paginated.map((order) => {
                                    const status = STATUS_LABELS[order.status] || { label: order.status, className: 'badge-ghost' };
                                    const payStatus = PAYMENT_STATUS_LABELS[order.paymentStatus || 'pending'] || { label: order.paymentStatus || '—', className: 'badge-ghost' };
                                    return (
                                        <tr key={order.id}>
                                            <td className="text-sm">
                                                {new Date(order.createdAt).toLocaleDateString('fr-FR', {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    year: 'numeric',
                                                })}
                                            </td>
                                            <td>
                                                {order.member ? (
                                                    <div>
                                                        <p className="font-medium">{order.member.firstName} {order.member.lastName}</p>
                                                        <p className="text-xs text-base-content/50">{order.member.email}</p>
                                                    </div>
                                                ) : (
                                                    <span className="text-sm text-base-content/50">{order.memberEmail}</span>
                                                )}
                                            </td>
                                            <td>
                                                {order.client ? (
                                                    <div>
                                                        <p className="font-medium">{order.client.firstName} {order.client.lastName}</p>
                                                        <p className="text-xs text-base-content/50">{order.client.email}</p>
                                                    </div>
                                                ) : (
                                                    <span className="text-base-content/50">—</span>
                                                )}
                                            </td>
                                            <td className="text-sm">
                                                <span className="font-medium">{getOrderTypeLabel(order)}</span>
                                                {isMultiProductOrder(order) && (
                                                    <span className="mt-0.5 block text-xs text-base-content/40">{order.items?.length || 0} article{(order.items?.length || 0) > 1 ? 's' : ''}</span>
                                                )}
                                            </td>
                                            <td className="max-w-[180px] truncate text-sm" title={getOrderFabricLabel(order)}>{getOrderFabricLabel(order)}</td>
                                            <td>
                                                <span className={`badge badge-sm ${status.className}`}>
                                                    {status.label}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`badge badge-sm ${payStatus.className}`}>
                                                    {payStatus.label}
                                                </span>
                                                {typeof order.amountTotal === 'number' && (
                                                    <div className="text-xs text-base-content/50 mt-0.5 tabular-nums">
                                                        {order.amountTotal.toFixed(2)} {(order.currency || 'EUR').toUpperCase() === 'EUR' ? '€' : (order.currency || 'EUR').toUpperCase()}{order.amountSubtotal != null ? ' TTC' : ''}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        onClick={() => setSelectedOrder(order)}
                                                        className="btn btn-ghost btn-xs gap-1"
                                                    >
                                                        <EyeIcon className="w-4 h-4" />
                                                        Voir
                                                    </button>
                                                    <button
                                                        onClick={() => handleDownloadPdf(order.id)}
                                                        className="btn btn-ghost btn-xs gap-1"
                                                    >
                                                        <ArrowDownTrayIcon className="w-4 h-4" />
                                                        PDF
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                        <span className="text-sm text-base-content/50">
                            {(page - 1) * rowsPerPage + 1}–{Math.min(page * rowsPerPage, filtered.length)} sur {filtered.length}
                        </span>
                        <div className="flex items-center gap-1">
                            <button
                                className="btn btn-ghost btn-sm"
                                disabled={page <= 1}
                                onClick={() => setPage(page - 1)}
                            >
                                <ChevronLeftIcon className="w-4 h-4" />
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                                .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
                                    if (idx > 0 && p - (arr[idx - 1]) > 1) acc.push('ellipsis');
                                    acc.push(p);
                                    return acc;
                                }, [])
                                .map((p, idx) =>
                                    p === 'ellipsis' ? (
                                        <span key={`e-${idx}`} className="px-1 text-base-content/30">…</span>
                                    ) : (
                                        <button
                                            key={p}
                                            className={`btn btn-sm ${page === p ? 'btn-primary' : 'btn-ghost'}`}
                                            onClick={() => setPage(p)}
                                        >
                                            {p}
                                        </button>
                                    )
                                )}
                            <button
                                className="btn btn-ghost btn-sm"
                                disabled={page >= totalPages}
                                onClick={() => setPage(page + 1)}
                            >
                                <ChevronRightIcon className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal détail commande */}
            <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} onDownloadPdf={handleDownloadPdf} downloadingPdfId={downloadingPdfId} />
        </Layout>
    );
};

// ============ Modal détail ============

const DetailRow: FC<{ label: string; value?: string | null }> = ({ label, value }) => {
    if (!value || value === 'none') return null;
    return (
        <div className="flex justify-between items-start py-1.5">
            <span className="text-sm text-base-content/60">{label}</span>
            <span className="text-sm font-medium text-right max-w-[60%]">{value}</span>
        </div>
    );
};

const DetailSection: FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="mb-4">
        <h4 className="text-xs font-semibold text-primary uppercase tracking-wider mb-2">{title}</h4>
        <div className="divide-y divide-base-200">{children}</div>
    </div>
);

const ConfigurationDetails: FC<{ item: PresentableOrderItem }> = ({ item }) => (
    <>
        {getConfigurationSections(item).map((section) => (
            <DetailSection key={section.label} title={section.label}>
                {section.rows.map(([label, value], index) => <DetailRow key={`${label}-${index}`} label={label} value={value} />)}
            </DetailSection>
        ))}
        {getMeasurementSections(item).map((section) => (
            <DetailSection key={`measurements-${section.label}`} title={`Mesures ${section.label}`}>
                {section.rows.map(([label, value]) => <DetailRow key={label} label={label} value={value} />)}
            </DetailSection>
        ))}
    </>
);

const MultiProductDetails: FC<{ items: OrderItem[]; supplierCurrency?: string; clientCurrency?: string }> = ({
    items, supplierCurrency, clientCurrency,
}) => (
    <>
        {items.map((item, index) => {
            const presentableItem: PresentableOrderItem = {
                id: item.id,
                productType: item.productType,
                configuration: item.configuration || {},
                measurements: item.measurements,
            };
            return (
                <DetailSection key={item.id} title={`Article ${index + 1} — ${PRODUCT_LABELS[item.productType]}`}>
                    <ConfigurationDetails item={presentableItem} />
                    {typeof item.supplierSubtotal === 'number' && (
                        <DetailRow label="Sous-total fournisseur HT" value={`${item.supplierSubtotal.toFixed(2)} ${(supplierCurrency || 'EUR').toUpperCase()}`} />
                    )}
                    {typeof (item.memberSubtotal ?? item.clientSubtotal) === 'number' && (
                        <DetailRow label="Prix client indicatif" value={`${Number(item.memberSubtotal ?? item.clientSubtotal).toFixed(2)} ${(clientCurrency || 'EUR').toUpperCase()}`} />
                    )}
                </DetailSection>
            );
        })}
    </>
);

const OrderDetailModal: FC<{ order: Order | null; onClose: () => void; onDownloadPdf: (id: string) => void; downloadingPdfId: string | null }> = ({ order, onClose, onDownloadPdf, downloadingPdfId }) => {
    if (!order) return null;

    const status = STATUS_LABELS[order.status] || { label: order.status, className: 'badge-ghost' };

    return (
        <dialog className="modal modal-open">
            <div className="modal-box max-w-2xl max-h-[85vh]">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="font-bold text-lg">Détail de la commande</h3>
                        <p className="text-xs text-base-content/50 mt-0.5">
                            {new Date(order.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            className={`btn btn-sm btn-outline gap-1 ${downloadingPdfId === order.id ? 'loading' : ''}`}
                            onClick={() => onDownloadPdf(order.id)}
                            disabled={downloadingPdfId === order.id}
                        >
                            <ArrowDownTrayIcon className="w-4 h-4" />
                            Exporter PDF
                        </button>
                        <span className={`badge ${status.className}`}>{status.label}</span>
                        <button className="btn btn-ghost btn-sm btn-circle" onClick={onClose}>
                            <XMarkIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <div className="overflow-y-auto max-h-[calc(85vh-8rem)] pr-1">
                    {/* Membre */}
                    <DetailSection title="Membre">
                        {order.member ? (
                            <>
                                <DetailRow label="Nom" value={`${order.member.firstName} ${order.member.lastName}`} />
                                <DetailRow label="Email" value={order.member.email} />
                            </>
                        ) : (
                            <DetailRow label="Email" value={order.memberEmail} />
                        )}
                    </DetailSection>

                    {/* Client */}
                    <DetailSection title="Client">
                        {order.client ? (
                            <>
                                <DetailRow label="Nom" value={`${order.client.firstName} ${order.client.lastName}`} />
                                <DetailRow label="Email" value={order.client.email} />
                                <DetailRow label="Téléphone" value={order.client.phone} />
                            </>
                        ) : (
                            <DetailRow label="Client" value="—" />
                        )}
                    </DetailSection>

                    {/* Tarification fournisseur */}
                    {typeof order.amountTotal === 'number' && (
                        <DetailSection title="Tarification fournisseur">
                            {typeof order.amountSubtotal === 'number' && (
                                <DetailRow label="Sous-total HT" value={`${order.amountSubtotal.toFixed(2)} ${(order.currency || 'EUR').toUpperCase()}`} />
                            )}
                            {order.dreamBox === true && (
                                <>
                                    <DetailRow label="DREAM BOX" value={`${(order.dreamBoxPrice ?? 29).toFixed(2)} ${(order.currency || 'EUR').toUpperCase()} HT`} />
                                    {typeof order.dreamBoxClientPrice === 'number' && <DetailRow label="Prix DREAM BOX client" value={`${order.dreamBoxClientPrice.toFixed(2)} ${(order.clientCurrency || 'EUR').toUpperCase()}`} />}
                                    {order.dreamBoxFabric && <DetailRow label="Tissu DREAM BOX" value={`${order.dreamBoxFabric}${order.dreamBoxLiasseNamespace ? ` (${order.dreamBoxLiasseNamespace})` : ''}`} />}
                                </>
                            )}
                            {typeof order.vatAmount === 'number' && (
                                <DetailRow label={`TVA (${Math.round(Number(order.vatRate ?? 0.2) * 100)} %)`} value={`${order.vatAmount.toFixed(2)} ${(order.currency || 'EUR').toUpperCase()}`} />
                            )}
                            <DetailRow label={order.amountSubtotal != null ? 'Total TTC' : 'Total'} value={`${order.amountTotal.toFixed(2)} ${(order.currency || 'EUR').toUpperCase()}`} />
                            {typeof order.clientAmountTotal === 'number' && (
                                <DetailRow label="Total client indicatif" value={`${order.clientAmountTotal.toFixed(2)} ${(order.clientCurrency || 'EUR').toUpperCase()}`} />
                            )}
                        </DetailSection>
                    )}

                    {isMultiProductOrder(order) ? (
                        <MultiProductDetails
                            items={order.items || []}
                            supplierCurrency={order.currency}
                            clientCurrency={order.clientCurrency}
                        />
                    ) : <ConfigurationDetails item={getLegacyOrderItem(order as unknown as Record<string, any>)} />}
                </div>
            </div>
            <form method="dialog" className="modal-backdrop">
                <button onClick={onClose}>close</button>
            </form>
        </dialog>
    );
};
